import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import FollowList from './FollowList';
import ReviewsList from './Reviews/ReviewsList';
import DynamicHeader from '../Header/DynamicHeader';
import ProfileActions from './ProfileActions';
import ListingDetails from '../CreateListing/ListingDetails';
import '../Feed/Post/PostCard.scss';
import GlassButton from '../UI/GlassButton';
// import Tips from "../Tips/Tips";
import './Profile.scss';
import { Helmet } from "react-helmet-async";
import { Sparkles } from "lucide-react";
import { trackOpenProfile, trackMessageUser } from '../../utils/trackActivity';

import listingsIcon from './img/listings.png';
import postsIcon from './img/posts.png';
import searchIcon from './img/search.png';
import storeIcon from './img/store.png';
import linkIcon from './img/link.png';
import locationIcon from './img/location.png';
import moreIcon from '../Header/img/moreIcon.png';

import {
  HARD_TTL,
  cacheGetStale,
  cacheSet,
} from '../../utils/swrcache';

import ProfileListing from './ProfileListing';
import ProfileSearch from './ProfileSearch';
import ProfilePosts from './ProfilePosts';
import ShowcasePage from './Showcase/ShowcasePage';
import ProfileCompletionHint from './ProfileCompletionHint';
import ShoppingProfileEntry from '../Settings/ShoppingProfile/ShoppingProfileEntry';

const DEFAULT_AVATAR_URL = 'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.hostname.includes('onrender.com')
    ? 'https://cvanto-api.onrender.com'
    : 'http://localhost:5000');

const API = process.env.REACT_APP_API || API_BASE;

function timeAgo(ts) {
  if (!ts) return '';
  const now = Date.now();
  const diffMs = Math.max(0, now - Number(ts));
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec} с тому`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} хв тому`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs} год тому`;
  const d = Math.floor(hrs / 24);
  if (d === 1) return 'вчора';
  if (d < 7) return `${d} дн тому`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} тиж тому`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} міс тому`;
  const y = Math.floor(mo / 12);
  return `${y} р тому`;
}

// Package #15 — Ukrainian plural form of "відгук" for count-aware copy
// (details in the stat tile's `title` tooltip, e.g. "12 відгуків").
// Same n%10/n%100 rule set Ukrainian/Russian share for this noun class.
export function pluralReviewsWord(count) {
  const n = Math.abs(Number(count) || 0);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'відгук';
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'відгуки';
  return 'відгуків';
}

// Formats the profile "Відгуки" stat tile. Deliberately separate from
// Trust's "Рейтинг довіри" — different data source (Users.sellerRating*
// vs. trust.js's algorithmic score), different label, never combined.
// count === 0 (or a missing/null average) renders as "—", not "0.0" or
// "★0.0" — a never-reviewed seller must read as "no data yet", not as a
// genuine bottom score.
export function formatSellerRatingStat(sellerRating) {
  const count = Number(sellerRating?.count || 0);
  const average = sellerRating?.average;

  if (!count || average == null) {
    return { value: '—', title: 'Ще немає відгуків' };
  }

  const avgStr = Number(average).toFixed(1);
  return {
    value: `★${avgStr}`,
    title: `${avgStr} (${count} ${pluralReviewsWord(count)})`,
  };
}

const SOCIAL_CATALOG = [
  { key: 'website',   label: 'Сайт',        emoji: '🌐',  test: (u) => u.website || u.site },
  { key: 'telegram',  label: 'Telegram',    emoji: '✈️',   test: (u) => u.telegram },
  { key: 'instagram', label: 'Instagram',   emoji: '📸',  test: (u) => u.instagram },
  { key: 'facebook',  label: 'Facebook',    emoji: 'f',   test: (u) => u.facebook },
  { key: 'tiktok',    label: 'TikTok',      emoji: '🎵',  test: (u) => u.tiktok },
  { key: 'youtube',   label: 'YouTube',     emoji: '▶️',  test: (u) => u.youtube },
  { key: 'linkedin',  label: 'LinkedIn',    emoji: 'in',  test: (u) => u.linkedin },
  { key: 'x',         label: 'X',           emoji: '𝕏',   test: (u) => u.x || u.twitter },
  { key: 'viber',     label: 'Viber',       emoji: '📞',  test: (u) => u.viber },
  { key: 'whatsapp',  label: 'WhatsApp',    emoji: '💬',  test: (u) => u.whatsapp },
];

function sanitizeUrl(raw) {
  if (!raw) return null;
  let url = String(raw).trim();
  const allowed = ['http://', 'https://', 'mailto:', 'tel:', 'tg://', 'viber://', 'whatsapp://'];
  const hasScheme = allowed.some(s => url.toLowerCase().startsWith(s));
  if (!hasScheme && !url.startsWith('/')) url = `https://${url}`;
  try { new URL(url); return url; } catch { return null; }
}
function normalizeLinks(user) {
  const out = [];
  const rawList = Array.isArray(user?.links) ? user.links : [];
  for (const entry of rawList) {
    if (typeof entry === 'string') {
      const url = sanitizeUrl(entry);
      if (url) out.push({ key: 'custom', url, label: 'Посилання', emoji: '🔗', visible: true });
    } else if (entry && typeof entry === 'object') {
      const url = sanitizeUrl(entry.url || entry.href || '');
      const visible = typeof entry.visible === 'boolean' ? entry.visible : true;
      if (url && visible !== false) {
        out.push({ key: entry.key || 'custom', url, label: entry.label || 'Посилання', emoji: '🔗', visible: true });
      }
    }
  }
  const socialObj = user?.socialLinks || user?.social || null;
  if (socialObj && typeof socialObj === 'object') {
    for (const cat of SOCIAL_CATALOG) {
      const val = socialObj[cat.key] ?? (cat.key === 'x' ? socialObj.twitter : null);
      if (val) {
        const url = sanitizeUrl(val);
        if (url) out.push({ key: cat.key, url, label: cat.label, emoji: cat.emoji, visible: true });
      }
    }
  }
  for (const cat of SOCIAL_CATALOG) {
    const val = cat.test(user);
    if (!val) continue;
    const url = sanitizeUrl(val);
    if (url && !out.some(x => x.url === url)) out.push({ key: cat.key, url, label: cat.label, emoji: cat.emoji, visible: true });
  }
  const uniq = [];
  const seen = new Set();
  for (const l of out) {
    if (l && l.url && !seen.has(l.url)) { seen.add(l.url); uniq.push(l); }
  }
  return uniq;
}

const TTL_24H = 24 * 60 * 60 * 1000;
const TTL_MIN = 60 * 1000;
const TTL_5MIN = 5 * 60 * 1000;

const Profile = () => {
  const { userId: paramUserId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userId: currentUserId } = useAuth();

  const effectiveUserId = paramUserId || currentUserId;
  const isOwner = !paramUserId || paramUserId === currentUserId;

  const [userName, setUserName] = useState('---');
  const [bio, setBio] = useState('');
  const [profileType, setProfileType] = useState('');
  const [avatar, setAvatar] = useState(DEFAULT_AVATAR_URL);
  // Read from the profile's own subscription field (returned by GET
  // /api/account/:id for any viewer, not just the owner) rather than the
  // logged-in viewer's own SubscriptionContext — this is what makes the
  // badge correct on both self-view and public-view of someone else's
  // profile. isBusiness is guarded defensively even though ProfileRouter
  // already routes Business accounts to BusinessProfile instead of here.
  const [profileIsPlus, setProfileIsPlus] = useState(false);
  const [createdAt, setCreatedAt] = useState('');
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const [isAvatarOpen, setIsAvatarOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showList, setShowList] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [locationStr, setLocationStr] = useState('');
  const [showLocation, setShowLocation] = useState(false);

  const [links, setLinks] = useState([]);

  const [listings, setListings] = useState(null);
  const [posts, setPosts] = useState(null);
  const [viewMode, setViewMode] = useState('listings');

  const [modalListingId, setModalListingId] = useState(null);
  const [isStaff, setIsStaff] = useState(false);

  const [trustScore, setTrustScore] = useState(null);
  const [trustTier, setTrustTier] = useState('novice');

  // Package #15 — derived from Users.sellerRatingSum/sellerRatingCount via
  // GET /api/account/:id's existing `sellerRating` field (server/routes/
  // account.js). A SEPARATE number from Trust above — never merged with
  // trustScore/trustTier, never renders under the "Рейтинг"/"Рейтинг
  // довіри" labels those use.
  const [sellerRating, setSellerRating] = useState(null);
  const [showReviews, setShowReviews] = useState(false);

  const [isMounted, setIsMounted] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);


    useEffect(() => {
    const t = requestAnimationFrame(() => setIsMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
  try {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    document.body.style.paddingRight = "";
    document.documentElement.style.overflow = "";
  } catch {}
}, [effectiveUserId]);

useEffect(() => {
  const targetUserId = String(effectiveUserId || '').trim();
  const viewerUserId = String(currentUserId || '').trim();

  if (!targetUserId) return;
  if (!viewerUserId) return;
  if (targetUserId === viewerUserId) return;
  if (trackedProfileRef.current === targetUserId) return;

  trackedProfileRef.current = targetUserId;

  trackOpenProfile({
    targetUserId,
  });
}, [effectiveUserId, currentUserId]);

  // DISABLED: логіка автопоказу Tips при відкритті профілю
  // useEffect(() => {
  //   if (!isOwner) return;
  //
  //   const seen = localStorage.getItem("profile:showcaseTipSeen");
  //
  //   if (!seen) {
  //     const t = setTimeout(() => {
  //       setShowShowcaseTip(true);
  //       localStorage.setItem("profile:showcaseTipSeen", "1");
  //     }, 650);
  //
  //     return () => clearTimeout(t);
  //   }
  // }, [isOwner]);

  const searchParams = new URLSearchParams(location.search);

  const deepTab =
    searchParams.get('tab') ||
    (searchParams.get('post') ? 'posts' : null);

  const deepItem = searchParams.get('item') || searchParams.get('post');

  const CK_PROFILE = effectiveUserId ? `profile:info:${effectiveUserId}` : null;
  const CK_STATS   = effectiveUserId && currentUserId ? `profile:stats:${effectiveUserId}:viewer:${currentUserId}` : null;

  const CK_STATS_PUBLIC = effectiveUserId ? `profile:stats:${effectiveUserId}:public` : null;

  const CK_BLOCK   = effectiveUserId && currentUserId ? `profile:block:${currentUserId}->${effectiveUserId}` : null;
  const CK_LISTS   = effectiveUserId ? `profile:listings:${effectiveUserId}` : null;
  const CK_POSTS   = effectiveUserId ? `profile:posts:${effectiveUserId}` : null;
  const CK_TRUST   = effectiveUserId ? `profile:trust:${effectiveUserId}` : null;

  const prevPathRef = useRef(null);
  const trackedProfileRef = useRef('');

  const isStaffRef = useRef(false);
  const lastFocusRefreshRef = useRef(0);

  useEffect(() => {
    isStaffRef.current = !!isStaff;
  }, [isStaff]);

  const inflightRef = useRef({
    profile: null,
    stats: null,
    block: null,
    lists: null,
    posts: null,
    trust: null,
  });

  const lastFetchAtRef = useRef({
    profile: 0,
    stats: 0,
    block: 0,
    lists: 0,
    posts: 0,
    trust: 0,
  });

  const safeJson = async (r) => {
    try { return await r.json(); } catch { return null; }
  };

  const fetchWithTimeout = async (url, options = {}, timeoutMs = 8000) => {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      return res;
    } finally {
      clearTimeout(t);
    }
  };

  const authFetch = async (url, options = {}) => {
    const token = await getAccessToken().catch(() => null);

    const headers = {
      ...(options.headers || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const authFetchWithTimeout = async (url, options = {}, timeoutMs = 8000) => {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const token = await getAccessToken().catch(() => null);

      const headers = {
        ...(options.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      return res;
    } finally {
      clearTimeout(t);
    }
  };

  const fetchProfileOnly = async ({ force = false } = {}) => {
    if (!effectiveUserId || !CK_PROFILE) return;
    if (!force && Date.now() - (lastFetchAtRef.current.profile || 0) < 1500) return;
    if (inflightRef.current.profile) return inflightRef.current.profile;

    const p = (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/account/${encodeURIComponent(effectiveUserId)}`, { cache: 'no-store' });
          if (res.ok) {
          const data = await res.json();
          setUserName(data.username || 'Користувач');
          setAvatar(data.avatar || DEFAULT_AVATAR_URL);
          setBio(data.bio || '');
          setProfileType(data.profileType || '');
          setCreatedAt(data.createdAt || '');
          setLocationStr(data.location || '');
          setShowLocation(!!data.showLocation);
          setLinks(normalizeLinks(data));
          setProfileIsPlus(!!data.subscription?.isPlus && !data.subscription?.isBusiness);
          setSellerRating(data.sellerRating || null);
          cacheSet(CK_PROFILE, data);

          try {
            if (data.avatar) localStorage.setItem("cvanto_avatar", data.avatar);
            if (data.theme === "dark" || data.theme === "light") {
              localStorage.setItem("cvanto_theme", data.theme);
            }
            if (data.language === "en" || data.language === "uk") {
              localStorage.setItem("cvanto_lang", data.language);
            }
          } catch {}
        }
      } catch {
      } finally {
        lastFetchAtRef.current.profile = Date.now();
        inflightRef.current.profile = null;
      }
    })();

    inflightRef.current.profile = p;
    return p;
  };

  const fetchStatsOnly = async ({ force = false } = {}) => {
    if (!effectiveUserId) return;

    const isSelfProfile = !!currentUserId && effectiveUserId === currentUserId;
    const key = (!currentUserId || isSelfProfile) ? CK_STATS_PUBLIC : CK_STATS;

    if (!key) return;
    if (!force && Date.now() - (lastFetchAtRef.current.stats || 0) < 1500) return;
    if (inflightRef.current.stats) return inflightRef.current.stats;

    const p = (async () => {
      try {
        const url =
          !currentUserId || isSelfProfile
            ? `${API_BASE}/api/follow/stats?target=${encodeURIComponent(effectiveUserId)}`
            : `${API_BASE}/api/follow/stats?viewer=${encodeURIComponent(currentUserId)}&target=${encodeURIComponent(effectiveUserId)}`;

        const res = await fetch(url, { cache: 'no-store' });

        if (res.ok) {
          const data = (await safeJson(res)) || {};
          setFollowers(data.followers || 0);
          setFollowing(data.following || 0);
          setIsFollowing(!currentUserId || isSelfProfile ? false : !!data.isFollowing);

          cacheSet(key, {
            followers: data.followers || 0,
            following: data.following || 0,
            isFollowing: !currentUserId || isSelfProfile ? false : !!data.isFollowing,
          });
        }
      } catch {
      } finally {
        lastFetchAtRef.current.stats = Date.now();
        inflightRef.current.stats = null;
      }
    })();

    inflightRef.current.stats = p;
    return p;
  };

  const fetchBlockOnly = async ({ force = false } = {}) => {
  if (!effectiveUserId || !currentUserId || !CK_BLOCK || effectiveUserId === currentUserId) return;
  if (!force && Date.now() - (lastFetchAtRef.current.block || 0) < 1500) return;
  if (inflightRef.current.block) return inflightRef.current.block;

  const p = (async () => {
    try {
      const res = await authFetch(
        `${API_BASE}/api/block/check/${encodeURIComponent(effectiveUserId)}?blockerId=${encodeURIComponent(currentUserId)}`,
        { cache: 'no-store' }
      );
      if (res.ok) {
        const data = (await safeJson(res)) || {};
        const blocked = !!data.isBlocked;
        setIsBlocked(blocked);
        if (blocked) setIsFollowing(false);
        cacheSet(CK_BLOCK, { isBlocked: blocked });
        if (blocked && CK_STATS) {
          const cStatsNow = cacheGetStale(CK_STATS, TTL_24H) || {};
          cacheSet(CK_STATS, {
            followers: cStatsNow.followers || 0,
            following: cStatsNow.following || 0,
            isFollowing: false,
          });
        }
      }
    } catch {
    } finally {
      lastFetchAtRef.current.block = Date.now();
      inflightRef.current.block = null;
    }
  })();

  inflightRef.current.block = p;
  return p;
};

    const fetchListingsOnly = async ({ force = false, silent = false } = {}) => {
    if (!effectiveUserId || !CK_LISTS) return;
    if (!force && Date.now() - (lastFetchAtRef.current.lists || 0) < 1500) return;
    if (inflightRef.current.lists) return inflightRef.current.lists;

    const p = (async () => {
      const cachedBefore = cacheGetStale(CK_LISTS, TTL_24H);

      try {
        const runFetch = async (timeoutMs) => {
          const res = await fetchWithTimeout(
            `${API_BASE}/api/listings/user/${encodeURIComponent(effectiveUserId)}`,
            { cache: 'no-store' },
            timeoutMs
          );

          if (!res || !res.ok) {
            throw new Error(`LISTINGS_HTTP_${res?.status || 'FAIL'}`);
          }

          const data = await res.json().catch(() => []);
          const base = (Array.isArray(data) ? data : []).filter(
            (it) => it.status !== 'archived' && it.status !== 'deleted'
          );

          if (!silent) {
            setListings(base);
          }

          cacheSet(CK_LISTS, base);

          if (!isStaffRef.current) {
  setListings(base);
  cacheSet(CK_LISTS, base);
  return base;
}

        const settled = await Promise.allSettled(
          base.map(async (it) => {
            const lid = String(it.listingId || it.id);

            try {
              const r = await authFetchWithTimeout(
                `${API_BASE}/api/reports/targets/listing/${encodeURIComponent(lid)}`,
                { cache: 'no-store' },
                7000
              );

              if (!r || !r.ok) return { ...it, __autoHidden: false };

              const j = await r.json().catch(() => ({}));
              return { ...it, __autoHidden: !!j?.aggregate?.autoHidden };
            } catch {
              return { ...it, __autoHidden: false };
            }
          })
        );

        const enriched = settled.map((s, idx) =>
          s.status === 'fulfilled' ? s.value : { ...base[idx], __autoHidden: false }
        );

        setListings(enriched);
        cacheSet(CK_LISTS, enriched);

        return enriched;
        };

        try {
          return await runFetch(15000);
        } catch {
          return await runFetch(25000);
        }
      } catch {
        if (Array.isArray(cachedBefore)) {
          setListings(cachedBefore);
          return cachedBefore;
        }

        if (!silent) {
          setListings(null);
        }

        return null;
      } finally {
        lastFetchAtRef.current.lists = Date.now();
        inflightRef.current.lists = null;
      }
    })();

    inflightRef.current.lists = p;
    return p;
  };

  const fetchPostsOnly = async ({ force = false } = {}) => {
    if (!effectiveUserId || !CK_POSTS) return;
    if (!force && Date.now() - (lastFetchAtRef.current.posts || 0) < 1500) return;
    if (inflightRef.current.posts) return inflightRef.current.posts;

    const p = (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/posts/user/${encodeURIComponent(effectiveUserId)}`, { cache: 'no-store' });

        if (res.ok) {
          const data = await res.json();
          const base = (Array.isArray(data) ? data : []).filter(
            (it) => it.status !== 'archived' && it.status !== 'deleted'
          );

          let savedIdsSet = new Set();
          try {
            if (currentUserId) {
              const savedToken = await getAccessToken().catch(() => null);
              const s = await fetch(`${API_BASE}/api/saved/${encodeURIComponent(currentUserId)}`, {
                cache: 'no-store',
                headers: { Authorization: `Bearer ${savedToken}` },
              });
              const arr = await s.json().catch(() => []);
              const ids = Array.isArray(arr) ? arr.map(it => it.postId) : [];
              savedIdsSet = new Set(ids.map(String));
            }
          } catch {}

          const enriched = await Promise.all(
            base.map(async (p) => {
              const pid = String(p.postId || p.id || p._id);
              const patch = {};

              try {
                const [cmtR, shrR, savR, likR] = await Promise.all([
                  fetch(`${API_BASE}/api/comments/stats/${encodeURIComponent(pid)}`, { cache: 'no-store' }),
                  fetch(`${API_BASE}/api/shares/count/${encodeURIComponent(pid)}`, { cache: 'no-store' }),
                  fetch(`${API_BASE}/api/saved/count/${encodeURIComponent(pid)}`, { cache: 'no-store' }),
                  authFetch(`${API_BASE}/api/likes/${encodeURIComponent(pid)}`, { cache: 'no-store' }),
                ]);

                const [cmt, shr, sav, lik] = await Promise.all([
                  cmtR.json().catch(() => ({})),
                  shrR.json().catch(() => ({})),
                  savR.json().catch(() => ({})),
                  likR.json().catch(() => ({})),
                ]);

                patch.commentsCount = Number(cmt?.count || p.commentsCount || 0);
                patch.sharesCount = Number(shr?.count || p.sharesCount || 0);
                patch.favoritesCount = Number(sav?.count || p.favoritesCount || 0);
                patch.likesCount = Number(lik?.count || p.likesCount || 0);

                if (currentUserId && Array.isArray(lik?.users)) {
                  patch.isLiked = lik.users.includes(currentUserId);
                }
              } catch {}

              patch.isSaved = savedIdsSet.has(pid);

              if (isStaffRef.current) {
              try {
                const r = await authFetch(
                  `${API_BASE}/api/reports/targets/post/${encodeURIComponent(pid)}`,
                  { cache: 'no-store' }
                );

                if (r && r.ok) {
                  const j = await r.json().catch(() => ({}));
                  patch.__autoHidden = !!j?.aggregate?.autoHidden;
                } else {
                  patch.__autoHidden = false;
                }
              } catch {
                patch.__autoHidden = false;
              }
            } else {
              patch.__autoHidden = false;
            }

              return { ...p, ...patch };
            })
          );

          const visible = isStaffRef.current ? enriched : enriched.filter(it => !it.__autoHidden);

          setPosts(visible);
          cacheSet(CK_POSTS, visible);
        } else {
          setPosts([]);
          cacheSet(CK_POSTS, []);
        }
      } catch {
        setPosts([]);
        cacheSet(CK_POSTS, []);
      } finally {
        lastFetchAtRef.current.posts = Date.now();
        inflightRef.current.posts = null;
      }
    })();

    inflightRef.current.posts = p;
    return p;
  };

  const fetchTrustOnly = async ({ force = false } = {}) => {
    if (!effectiveUserId || !CK_TRUST) return;
    if (!force && Date.now() - (lastFetchAtRef.current.trust || 0) < 1500) return;
    if (inflightRef.current.trust) return inflightRef.current.trust;

    const p = (async () => {
      try {
        const res = await authFetch(`${API}/api/trust/${encodeURIComponent(effectiveUserId)}/summary`, {
          cache: 'no-store',
        });

        if (res.ok) {
          const data = await res.json();

          setTrustScore(Number(data?.trustScore ?? 0));
          setTrustTier(data?.trustTier || 'novice');

          cacheSet(CK_TRUST, {
            trustScore: Number(data?.trustScore ?? 0),
            trustTier: data?.trustTier || 'novice',
          });
        } else {
          setTrustScore(null);
          setTrustTier('novice');
        }
      } catch {
        setTrustScore(null);
        setTrustTier('novice');
      } finally {
        lastFetchAtRef.current.trust = Date.now();
        inflightRef.current.trust = null;
      }
    })();

    inflightRef.current.trust = p;
    return p;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!currentUserId) { if (alive) setIsStaff(false); return; }
        const r = await authFetch(`${API_BASE}/api/admin/me`, {
          cache: 'no-store',
        });
        const j = await r.json().catch(() => ({}));
        const ok = !!(j?.data?.isAdmin || j?.data?.isMod);
        if (alive) setIsStaff(ok);
      } catch {
        if (alive) setIsStaff(false);
      }
    })();
    return () => { alive = false; };
  }, [currentUserId]);

  useEffect(() => {
    if (!effectiveUserId) return;
    let cancelled = false;

    const hydrateFromCache = () => {
      const cProf = cacheGetStale(CK_PROFILE, TTL_24H);
      if (cProf && !cancelled) {
        setUserName(cProf.username || 'Користувач');
        setAvatar(cProf.avatar || DEFAULT_AVATAR_URL);
        setBio(cProf.bio || '');
        setProfileType(cProf.profileType || '');
        setCreatedAt(cProf.createdAt || '');
        setLocationStr(cProf.location || '');
        setShowLocation(!!cProf.showLocation);
        setLinks(normalizeLinks(cProf));
        setProfileIsPlus(!!cProf.subscription?.isPlus && !cProf.subscription?.isBusiness);
        setSellerRating(cProf.sellerRating || null);
      }

      const cStats = cacheGetStale(CK_STATS, TTL_24H);
      if (cStats && !cancelled) {
        setFollowers(cStats.followers || 0);
        setFollowing(cStats.following || 0);
        setIsFollowing(!!cStats.isFollowing);
      }

      const cStatsPublic = cacheGetStale(CK_STATS_PUBLIC, TTL_24H);
      if (cStatsPublic && !cancelled && !currentUserId) {
        setFollowers(cStatsPublic.followers || 0);
        setFollowing(cStatsPublic.following || 0);
        setIsFollowing(false);
      }

      if (currentUserId && effectiveUserId === currentUserId && !cancelled) {
        setIsBlocked(false);
      }

      const cBlock = cacheGetStale(CK_BLOCK, TTL_24H);
      if (cBlock && !cancelled && !(currentUserId && effectiveUserId === currentUserId)) {
        const blocked = !!cBlock.isBlocked;
        setIsBlocked(blocked);
        if (blocked) setIsFollowing(false);
      }

      const cListings = cacheGetStale(CK_LISTS, HARD_TTL);
      if (cListings !== undefined && cListings !== null && !cancelled) {
        setListings(cListings);
      }

      const cPosts = cacheGetStale(CK_POSTS, HARD_TTL);
      if (cPosts !== undefined && cPosts !== null && !cancelled) {
        setPosts(cPosts);
      }

      const cTrust = cacheGetStale(CK_TRUST, TTL_24H);
      if (cTrust && !cancelled) {
        setTrustScore(cTrust.trustScore);
        setTrustTier(cTrust.trustTier || 'novice');
      }
    };

      const fetchFresh = async () => {
      await fetchProfileOnly({ force: true });
      await fetchStatsOnly({ force: true });
      await fetchBlockOnly({ force: true });
      await fetchListingsOnly({ force: true });
    };

    hydrateFromCache();
    fetchFresh();

    return () => {
      cancelled = true;
    };
    }, [effectiveUserId, currentUserId, viewMode]);

  useEffect(() => {
    if (viewMode !== 'posts') return;
    if (!effectiveUserId) return;

    const cPosts = cacheGetStale(CK_POSTS, HARD_TTL);
    if (cPosts !== undefined && cPosts !== null) {
      setPosts(cPosts);
    }

    fetchPostsOnly({ force: true });
  }, [viewMode, effectiveUserId, currentUserId]);

    useEffect(() => {
    if (!effectiveUserId) return;
    if (viewMode !== 'listings' && viewMode !== 'search') return;

    const cListings = cacheGetStale(CK_LISTS, HARD_TTL);
    if (cListings !== undefined && cListings !== null) {
      setListings(cListings);
    }

    fetchListingsOnly({ force: true, silent: false });
  }, [viewMode, effectiveUserId]);

  useEffect(() => {
    if (!effectiveUserId) return;

    const cTrust = cacheGetStale(CK_TRUST, TTL_24H);
    if (cTrust) {
      setTrustScore(cTrust.trustScore);
      setTrustTier(cTrust.trustTier || 'novice');
    }

    fetchTrustOnly({ force: true });
  }, [effectiveUserId]);

  useEffect(() => {
    const onFocus = () => {
      if (!effectiveUserId) return;

      const now = Date.now();
      if (now - lastFocusRefreshRef.current < 30000) return;
      lastFocusRefreshRef.current = now;

      fetchProfileOnly({ force: true });
      fetchStatsOnly({ force: true });
      fetchBlockOnly({ force: true });

      if (viewMode === 'listings' || viewMode === 'search') {
        fetchListingsOnly({ force: true, silent: false });
      }
    };

    const onVis = () => {
      if (document.visibilityState === 'visible') onFocus();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
    }, [effectiveUserId, currentUserId, viewMode]);

  useEffect(() => {
    if (!deepTab || !deepItem) return;
    if (deepTab === 'listings') setViewMode('listings');
    else if (deepTab === 'posts') setViewMode('posts');
  }, [deepTab, deepItem]);

  useEffect(() => {
    if (!deepTab || !deepItem) return;
    if (deepTab === 'listings') {
      if (!Array.isArray(listings)) return;
      const found = listings.find(x => String(x.listingId || x.id) === String(deepItem));
      if (found) setModalListingId(found.listingId || found.id);
    }
  }, [deepTab, deepItem, listings]);

  const handleAvatarClick = () => setIsAvatarOpen(true);
  const handleCloseAvatar = () => {
    setIsClosing(true);
    setTimeout(() => { setIsAvatarOpen(false); setIsClosing(false); }, 300);
  };

  const handleMessageUserClick = () => {
    const targetUserId = String(effectiveUserId || paramUserId || '').trim();

    if (!targetUserId) return;

    trackMessageUser({
      targetUserId,
    });
  };

  const handleFollowToggle = async () => {
    if (!currentUserId || !paramUserId) return navigate('/login');
    if (followBusy || isBlocked) return;

    const prevFollowing = isFollowing;
    const prevFollowers = followers;

    const nextFollowing = !prevFollowing;
    const nextFollowers = Math.max(0, prevFollowers + (nextFollowing ? 1 : -1));

    setFollowBusy(true);

    setIsFollowing(nextFollowing);
    setFollowers(nextFollowers);

    if (CK_STATS) {
      const cached = cacheGetStale(CK_STATS, TTL_24H) || {};
      cacheSet(CK_STATS, {
        followers: nextFollowers,
        following: cached.following || 0,
        isFollowing: nextFollowing,
      });
    }
    if (CK_STATS_PUBLIC) {
      const cachedP = cacheGetStale(CK_STATS_PUBLIC, TTL_24H) || {};
      cacheSet(CK_STATS_PUBLIC, {
        followers: nextFollowers,
        following: cachedP.following || 0,
      });
    }

    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(`${API_BASE}/api/follow/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ followerId: currentUserId, followedId: paramUserId }),
      });
      await res.json().catch(() => ({}));

      if (res.ok) {
        await fetchStatsOnly({ force: true });
      } else {
        setIsFollowing(prevFollowing);
        setFollowers(prevFollowers);
        if (CK_STATS) {
          cacheSet(CK_STATS, {
            followers: prevFollowers,
            following: (cacheGetStale(CK_STATS, TTL_24H) || {}).following || 0,
            isFollowing: prevFollowing,
          });
        }
        if (CK_STATS_PUBLIC) {
          cacheSet(CK_STATS_PUBLIC, {
            followers: prevFollowers,
            following: (cacheGetStale(CK_STATS_PUBLIC, TTL_24H) || {}).following || 0,
          });
        }
      }
    } catch {
      setIsFollowing(prevFollowing);
      setFollowers(prevFollowers);
      if (CK_STATS) {
        cacheSet(CK_STATS, {
          followers: prevFollowers,
          following: (cacheGetStale(CK_STATS, TTL_24H) || {}).following || 0,
          isFollowing: prevFollowing,
        });
      }
      if (CK_STATS_PUBLIC) {
        cacheSet(CK_STATS_PUBLIC, {
          followers: prevFollowers,
          following: (cacheGetStale(CK_STATS_PUBLIC, TTL_24H) || {}).following || 0,
        });
      }
    } finally {
      setFollowBusy(false);
    }
  };

  const handleUnblock = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/block`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blockerId: currentUserId, blockedId: effectiveUserId }),
      });
      if (!res.ok) throw new Error('Не вдалося розблокувати');
      setIsBlocked(false);
      if (CK_BLOCK) cacheSet(CK_BLOCK, { isBlocked: false });
      await fetchBlockOnly({ force: true });
      await fetchStatsOnly({ force: true });
    } catch {}
  };

  const handleBlockedChange = (blocked) => {
    const nextBlocked = !!blocked;
    setIsBlocked(nextBlocked);
    if (nextBlocked) {
      setIsFollowing(false);
      if (CK_STATS) {
        const cStatsNow = cacheGetStale(CK_STATS, TTL_24H) || {};
        cacheSet(CK_STATS, {
          followers: cStatsNow.followers || 0,
          following: cStatsNow.following || 0,
          isFollowing: false,
        });
      }
    }
    if (CK_BLOCK) cacheSet(CK_BLOCK, { isBlocked: nextBlocked });
  };

  const getProfileTypeDisplay = (type) => {
    switch (type) {
      case 'services': return 'Послуги';
      case 'handmade': return 'Ручна робота';
      case 'products': return 'Товари';
      case 'viewer': return 'Спостерігач';
      case 'education': return 'Освіта';
      default: return '';
    }
  };

  const openListingModalWithURL = (id) => {
    const prev = window.location.pathname + window.location.search;
    prevPathRef.current = prev;
    try {
      window.history.pushState(
        { modalFrom: 'profile', restore: prev, listingId: String(id) },
        '',
        `/listing/${encodeURIComponent(id)}`
      );
    } catch {}
    setModalListingId(id);
  };

  const closeListingModalAndRestoreURL = () => {
    const restore =
      (window.history.state && window.history.state.restore) ||
      prevPathRef.current ||
      (paramUserId ? `/profile/${paramUserId}` : '/profile');

    setModalListingId(null);
    try {
      window.history.replaceState(null, '', restore);
    } catch {}
  };

  const handleListingDeleted = (deletedId) => {
    setListings((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.filter(
        (ad) => String(ad.listingId || ad.id) !== String(deletedId)
      );
    });
    if (CK_LISTS) {
      const cur = cacheGetStale(CK_LISTS, HARD_TTL);
      if (Array.isArray(cur)) {
        cacheSet(CK_LISTS, cur.filter((ad) => String(ad.listingId || ad.id) !== String(deletedId)));
      }
    }
    closeListingModalAndRestoreURL();
  };

  useEffect(() => {
    const onPop = () => { if (modalListingId) setModalListingId(null); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [modalListingId]);

  const renderTrustBadge = () => {
    if (trustScore === null) return null;
    const lvl10 = (Math.round(Number(trustScore)) / 10).toFixed(1);
    const label = trustTier === 'trusted' ? 'Надійний'
                 : trustTier === 'verified' ? 'Перевірений'
                 : 'Новачок';
    return (
      <span className={`trust-badge trust-badge--${trustTier}`} title={`Рейтинг: ${lvl10}/10`}>
        {label}
      </span>
    );
  };

  const segIndex =
  viewMode === 'listings'
    ? 0
    : viewMode === 'search'
      ? 1
      : 2;

  const rightNode = isOwner ? (
    <button
      type="button"
      className="dh__menu"
      onClick={() => navigate('/settings')}
      aria-label="Налаштування"
    >
      <img
        src={moreIcon}
        alt=""
        aria-hidden="true"
        className="no-invert profile-header-menu-icon"
        style={{ display: 'block' }}
      />
    </button>
  ) : (
    <button
      type="button"
      className="dh__menu"
      onClick={() => setShowActions(true)}
      aria-label="Меню"
    >
      <img
        src={moreIcon}
        alt=""
        aria-hidden="true"
        className="no-invert profile-header-menu-icon"
        style={{ display: 'block' }}
      />
    </button>
  );

  const listingsCount = Array.isArray(listings) ? listings.length : 0;
  const listingsLoaded = Array.isArray(listings);

  const sellerRatingDisplay = formatSellerRatingStat(sellerRating);

  const origin =
    (typeof window !== "undefined" && window.location && window.location.origin)
      ? window.location.origin
      : "https://cvanto.com";

  const canonicalUrl = `${origin}/profile/${encodeURIComponent(effectiveUserId || "")}`;

  const seoTitle = userName && userName !== "---"
    ? `${userName} — Cvanto`
    : "Профіль — Cvanto";

  const bioClean = (bio || "").trim().replace(/\s+/g, " ").slice(0, 160);

  const seoDesc = bioClean
    ? bioClean
    : profileType
      ? `${getProfileTypeDisplay(profileType)} на Cvanto`
      : "Профіль користувача на Cvanto";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: userName && userName !== "---" ? userName : "Користувач",
    url: canonicalUrl,
    image: avatar || DEFAULT_AVATAR_URL,
    description: bioClean || undefined,
  };

  if (showLocation && locationStr) {
    jsonLd.homeLocation = {
      "@type": "Place",
      name: locationStr,
    };
  }

  if (links && links.length > 0) {
    jsonLd.sameAs = links.map(l => l.url).filter(Boolean);
  }

  return (
    <div className={`profile ${isMounted ? 'is-mounted' : ''}`}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={avatar || DEFAULT_AVATAR_URL} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={avatar || DEFAULT_AVATAR_URL} />

        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

            <DynamicHeader
        title={userName || 'Користувач'}
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        showMenu={false}
        rightNode={rightNode}
        withSpacer={true}
      />

      <div className="profile-aura" aria-hidden="true">
        <img
          src={avatar || DEFAULT_AVATAR_URL}
          alt=""
          className="profile-aura__image"
          onLoad={(e) => {
            e.currentTarget.classList.add('is-loaded');
          }}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = DEFAULT_AVATAR_URL;
          }}
        />
      </div>

      {/* Tips overlay при відкритті профілю */}
      {/* {showShowcaseTip && (
        <>
          <div className="profileSpotlightOverlay" />

          <div className="tipsIslandWrap">
            <Tips
              variant="profileShowcaseIntro"
              className="tipsCard--island"
              remember={false}
              onClose={() => setShowShowcaseTip(false)}
            />
          </div>
        </>
      )} */}

      <div className="profile-content">
        <div className="avatar-section-wrap">
          <div className="avatar-section">
            <img
              src={avatar}
              alt={userName && userName !== "---" ? `${userName} — фото профілю` : "Фото профілю"}
              onClick={handleAvatarClick}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = DEFAULT_AVATAR_URL;
              }}
            />
          </div>

          {profileIsPlus && (
            <span className="profile-plus-badge" title="Cvanto Plus">
              <Sparkles size={13} strokeWidth={2.5} />
            </span>
          )}
        </div>

        {isAvatarOpen && (
          <div className={`avatar-modal ${isClosing ? 'closing' : ''}`} onClick={handleCloseAvatar}>
            <img
              src={avatar}
              alt={userName && userName !== "---" ? `${userName} — фото профілю` : "Фото профілю"}
              className="avatar-modal__image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className="user-info">
          <h2>
            {userName}
            {renderTrustBadge()}
          </h2>
          {profileType && <p className="profile-type">{getProfileTypeDisplay(profileType)}</p>}
          {bio && <p className="profile-bio">{bio}</p>}
          {showLocation && locationStr && (
            <p className="profile-location">
              <img src={locationIcon} alt="" aria-hidden="true" className="location-icon no-invert" />
              <span>{locationStr}</span>
            </p>
          )}

          {links.length > 0 && (
            <div className="profile-links" role="list">
              {links.map((l, idx) => (
                <a
                  key={l.url + ':' + idx}
                  className="link-pill"
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  role="listitem"
                  aria-label={l.label}
                >
                  {l.key === 'custom' ? (
                    <img
                      className="pill-icon no-invert"
                      src={linkIcon}
                      alt=""
                      aria-hidden="true"
                    />
                  ) : (
                    <span className="pill-emoji" aria-hidden="true">{l.emoji || '🔗'}</span>
                  )}
                  <span className="pill-label">{l.label}</span>
                </a>
              ))}
            </div>
          )}

          {isOwner ? (
            <div className="action-buttons">
              <GlassButton onClick={() => navigate('/add-ad')} size="md">Створити</GlassButton>
              <GlassButton onClick={() => navigate('/edit-profile')} size="md" variant="neutral">
                Редагувати
              </GlassButton>
            </div>
          ) : (
            <div className="action-buttons">
              {isBlocked ? (
                <GlassButton
                  onClick={handleUnblock}
                  size="md"
                  variant="ghost"
                >
                  Розблокувати
                </GlassButton>
              ) : (
                <>
                  <GlassButton
                    onClick={handleFollowToggle}
                    size="md"
                    active={isFollowing}
                    disabled={followBusy}
                  >
                    {followBusy
                      ? '...'
                      : isFollowing
                        ? 'Відстежується'
                        : 'Стежити'}
                  </GlassButton>
                  <GlassButton
                    as="link"
                    to={`/messages?to=${encodeURIComponent(effectiveUserId)}`}
                    size="md"
                    variant="neutral"
                    onClick={handleMessageUserClick}
                  >
                    Повідомлення
                  </GlassButton>
                </>
              )}
            </div>
          )}

          <div className="stats">
            <div className="stat" onClick={() => setShowList('followers')} style={{ cursor: 'pointer' }}>
              <span>Підписники</span>
              <p>{followers}</p>
            </div>
            <div className="stat" onClick={() => setShowList('following')} style={{ cursor: 'pointer' }}>
              <span>Підписки</span>
              <p>{following}</p>
            </div>
            <div className="stat">
              <span>Оголошення</span>
              <p>{listingsCount}</p>
            </div>
            <div
              className="stat stat--rating"
              title={trustScore !== null ? `Рейтинг ${ (Math.round(trustScore)/10).toFixed(1) }/10` : 'Рейтинг'}
            >
              <span>Рейтинг</span>
              <p>{trustScore === null ? '—' : `${(Math.round(trustScore)/10).toFixed(1)}/10`}</p>
            </div>
            <div
              className="stat stat--reviews"
              title={sellerRatingDisplay.title}
              onClick={() => setShowReviews(true)}
              style={{ cursor: 'pointer' }}
            >
              <span>Відгуки</span>
              <p>{sellerRatingDisplay.value}</p>
            </div>
          </div>
        </div>

        <div
          className="content-switch content-switch--glass seg"
          style={{ '--seg-index': segIndex }}
          role="tablist"
          aria-label="Перемикач контенту профілю"
        >
          <div className="seg__indicator" aria-hidden="true" />

          <button
            className={`seg__btn ${viewMode === 'listings' ? 'active' : ''}`}
            onClick={() => setViewMode('listings')}
            role="tab"
            aria-selected={viewMode === 'listings'}
            aria-label="Оголошення"
            title="Оголошення"
          >
            <img src={listingsIcon} alt="" aria-hidden="true" className="no-invert" />
            <span className="sr-only">Оголошення</span>
            <span className="seg__label">Оголошення</span>
          </button>

          <button
            className={`seg__btn ${viewMode === 'search' ? 'active' : ''}`}
            onClick={() => setViewMode('search')}
            role="tab"
            aria-selected={viewMode === 'search'}
            aria-label="Шукаю"
            title="Шукаю"
          >
            <img src={searchIcon} alt="" aria-hidden="true" className="no-invert" />
            <span className="sr-only">Шукаю</span>
            <span className="seg__label">Шукаю</span>
          </button>

          <button
            className={`seg__btn ${viewMode === 'posts' ? 'active' : ''}`}
            onClick={() => setViewMode('posts')}
            role="tab"
            aria-selected={viewMode === 'posts'}
            aria-label="Пости"
            title="Пости"
          >
            <img src={postsIcon} alt="" aria-hidden="true" className="no-invert" />
            <span className="sr-only">Пости</span>
            <span className="seg__label">Пости</span>
          </button>
        </div>

      </div>

          <ProfileCompletionHint
          isOwner={isOwner}
          username={userName}
          avatar={avatar}
          bio={bio}
          listingsCount={listingsCount}
          listingsLoaded={listingsLoaded}
          onOpenEditProfile={() => navigate('/edit-profile')}
          onOpenCreateListing={() => navigate('/add-ad')}
        />

      <ShoppingProfileEntry isOwner={isOwner} />

      {viewMode === 'listings' && (
        <ProfileListing
          listings={listings}
          isStaff={isStaff}
          deepItem={deepItem}
          onOpenListing={(id) => openListingModalWithURL(id)}
          userId={effectiveUserId}
        />
      )}

      {viewMode === 'search' && (
        <ProfileSearch
          listings={listings}
          isStaff={isStaff}
          deepItem={deepItem}
          onOpenListing={(id) => openListingModalWithURL(id)}
          userId={effectiveUserId}
        />
      )}

      {viewMode === 'posts' && (
        <ProfilePosts
          posts={posts}
          userMeta={{ userName, avatar, effectiveUserId }}
          onPatchPosts={(updater) => setPosts(updater)}
          currentUserId={currentUserId}
          apiBase={API}
          timeAgo={timeAgo}
          deepItem={deepItem}
          userId={effectiveUserId}
        />
      )}

      {showList && (
        <FollowList
          userId={effectiveUserId}
          type={showList}
          onClose={() => setShowList(null)}
          currentUserId={currentUserId}
        />
      )}

      {showReviews && (
        <ReviewsList
          sellerId={effectiveUserId}
          currentUserId={currentUserId}
          isOwner={isOwner}
          onClose={() => setShowReviews(false)}
          onAggregateChange={() => fetchProfileOnly({ force: true })}
        />
      )}

      {showActions && (
        <ProfileActions
          onClose={() => setShowActions(false)}
          userInfo={{ id: effectiveUserId, username: userName, avatar, createdAt }}
          onBlockedChange={handleBlockedChange}
        />
      )}

      {modalListingId && (
        <ListingDetails
          listingId={modalListingId}
          isModal={true}
          onClose={closeListingModalAndRestoreURL}
          onDeleted={handleListingDeleted}
        />
      )}
    </div>
  );
};

export default Profile;